import {
  SlidingDrawer,
  Iframe,
  Button,
  SvgIcon,
  SvgNames,
} from '@sunrun/experience-ui-components';
import { getEnvironment } from '@sunrun/sales-experience-shared';
import styled from '@emotion/styled/macro';
import { useCallback } from 'react';
import { getCustomerProfileQuery } from 'helpers/listViewHelpers';

interface CustomerProfileDrawerProps {
  isOpen: boolean;
  onClose: (options?: { refreshPipeline?: boolean }) => void;
  prospectId: string;
  openPendingTask?: boolean;
  markClosedLost?: boolean;
}

const CLOSE_CUSTOMER_PROFILE_DRAWER = 'COMMAND:CLOSE_CUSTOMER_PROFILE_DRAWER';

const CustomerProfileDrawer = ({
  isOpen,
  onClose,
  prospectId,
  openPendingTask = false,
  markClosedLost = false,
}: CustomerProfileDrawerProps) => {
  const env = getEnvironment();

  const baseUrl =
    env === 'production'
      ? 'https://customer-profile.sunrunone.com/'
      : 'https://customer-profile.staging.sunrunone.com/';

  const oneBaseUrl =
    env === 'production'
      ? 'https://sunrunone.com'
      : 'https://staging.sunrunone.com';

  const profileTabQuery = {
    initialTab: markClosedLost ? 'account' : 'status',
    openPendingTask,
    markClosedLost,
  };
  const customerProfileQuery = getCustomerProfileQuery({
    ...profileTabQuery,
    hideTopNavigation: true,
  });
  const customerProfileUrl = `${baseUrl}${prospectId}${customerProfileQuery}`;
  const fullProfileUrl = `${oneBaseUrl}/customers/customer-profile/${prospectId}${getCustomerProfileQuery(
    profileTabQuery,
  )}`;

  const handleNavigateToProfile = () => {
    if ('ReactNativeWebView' in window) {
      window.parent.postMessage(
        {
          type: 'COMMAND:CHANGE_URL',
          source: 'Dashboard',
          payload: { url: fullProfileUrl },
        },
        '*',
      );
      return;
    }

    window.open(fullProfileUrl, '_blank');
  };

  const handleIframeMessage = useCallback(
    (event: MessageEvent) => {
      if (
        event.data?.type === CLOSE_CUSTOMER_PROFILE_DRAWER &&
        event.data?.source === 'CustomerProfile'
      ) {
        onClose({
          refreshPipeline: Boolean(event.data?.payload?.refreshPipeline),
        });
      }
    },
    [onClose],
  );

  return (
    <SlidingDrawer
      isOpen={isOpen}
      onClose={() => onClose()}
      closeOnOutsideClick={true}
      closeOnEscape={true}
      styles={{
        width: '800px',
        maxWidth: '100%',
      }}>
      <DrawerHeader>
        <DrawerButtons>
          {!markClosedLost && (
            <Button
              color="secondary"
              onClick={handleNavigateToProfile}
              size="sm">
              Go to customer profile
            </Button>
          )}
          <Button
            color="primary"
            onClick={() => onClose()}
            size="sm"
            iconMiddle={
              <SvgIcon
                name={SvgNames.Close}
                color="white"
                width="12"
                height="12"
              />
            }
          />
        </DrawerButtons>
      </DrawerHeader>
      <Iframe
        src={customerProfileUrl}
        parent="Dashboard"
        child="CustomerProfile"
        onMessage={handleIframeMessage}
        iframeProps={{
          title: 'Customer Profile',
          style: {
            width: '100%',
            height: '100%',
            border: 'none',
          },
        }}
      />
    </SlidingDrawer>
  );
};

const DrawerHeader = styled.div`
  position: absolute;
  top: 48px;
  right: 48px;
  z-index: 1000;

  @media (max-width: 600px) {
    top: 24px;
    right: 24px;
  }
`;

const DrawerButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export { CustomerProfileDrawer };
